<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :title="$t('Bulk Edit Invoices')"
                   @hidden="onHidden" :content-in-form="false" modal-class="mobile-full-screen"
                   :show-confirmation="showConfirmation" scrollable :data="model">

      <div class="row">
        <div class="col-md-12">
          <table class="info-table table table-sm invoices-table">
            <tbody>
            <tr>
              <th class="border-top-0">{{ $t('Invoice') }}</th>
            </tr>
            <tr v-for="(invoice, index) in modal.invoices" :key="`invoice-row-${index}`">
              <td>
                <a href="javascript:void(0)"
                   @click="onInvoiceViewClick(invoice)">
                  {{ invoice.invoice_no }}
                </a>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <b-button class="p-0 mb-2" variant="link" @click="onNewMiscellaneousItemClick">
            <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
            {{ $t('Add New Charge') }}
          </b-button>
        </div>
        <b-form @submit.prevent="onSubmit">
          <div class="col-md-12" v-if="model.miscellaneous_items.length > 0">
            <table class="info-table table table-sm miscellaneous-table">
              <tr>
                <th style="width: 25%">{{ $t('Description') }}</th>
                <th style="width: 25%">{{ $t('Comment') }}</th>
                <th style="width: 25%">{{ $t('Amount') }}</th>
                <th style="width: 15%" class="text-center">{{ $t('Taxable') }}</th>
                <th style="width: 15%" class="text-center">{{ $t('Actions') }}</th>
              </tr>
              <tr :key="`miscellaneous-item-${index}`" v-for="(item, index) in model.miscellaneous_items">
                <td class="d-flex pr-3">
                  <input-widget v-if="hasPredefinedMiscCharges" style="flex: 1" :items="invoiceMiscellaneousItemCharges"
                                value-field="key"
                                display-field="${item.value}"
                                @select2Select="onMiscChargeDescriptionDropdownValueChange(item)"
                                type="select2" :model="item" :label="false" attribute="title_dropdown_value"
                                :rules="item.amount ? 'required' : ''"/>
                  <input-widget v-if="item.title_dropdown_value === 'other' || !hasPredefinedMiscCharges"
                                style="flex: 1"
                                :class="{'ml-2': hasPredefinedMiscCharges}"
                                :rules="item.amount ? 'required|max:255' : 'max:255'"
                                :model="item" attribute="title" :label="false"/>
                </td>
                <td class="pr-3">
                  <input-widget value-field="key"
                                display-field="${item.value}"
                                type="text" :model="item" :label="false" attribute="comment"/>
                </td>
                <td class="pr-3">
                  <currency-input-widget :rules="item.title ? 'required' : ''" :model="item" attribute="amount"
                                         :label="false"/>
                </td>
                <td class="text-center">
                  <input-widget attribute="taxable" :model="item" type="checkbox" :label="false"/>
                </td>
                <td class="text-center">
                  <b-tooltip :target="`remove-commission-button-${index}`" :title="$t('Delete')"/>
                  <font-awesome-icon class="text-danger hover-cursor" :id="`remove-commission-button-${index}`"
                                     @click="onMiscellaneousItemDelete(index)" style="font-size: 1.5em;"
                                     v-if="model.miscellaneous_items.length > 1"
                                     :icon="'times'"/>
                </td>
              </tr>
            </table>
          </div>
        </b-form>
      </div>
      <template v-slot:modal-footer>
        <cancel-button @click="hideBulkEditInvoicesModal" :disabled="loading">
          {{ $t('Cancel') }}
        </cancel-button>
        <submit-button @click="handleSubmit(onSubmit)" :show-loader="loading"
                       :disabled="model.miscellaneous_items.length === 0">
          {{ $t('Submit') }}
        </submit-button>
      </template>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import {InvoiceModalMixin} from "@/modules/invoices/modals/invoiceModal.mixin";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget.vue";
  import InputWidget from "@/core/components/input-widget/InputWidget.vue";
  import InvoicesService from "@/modules/invoices/InvoicesService";
  import MiscellaneousItem from "@/modules/invoices/models/MiscellaneousItem.model";
  import {INVOICE_TYPE_CREDIT_NOTE, WS_BULK_EDIT_INVOICES} from "@/modules/constants";
  import {ON_PDF_MODAL_CLOSE, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import SubmitButton from "@/core/components/common/SubmitButton.vue";
  import CancelButton from "@/core/components/common/CancelButton.vue";
  import BulkEditInvoiceModel from "@/modules/invoices/models/BulkEditInvoiceModel.model";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {mapActions: mapGlobalActions, mapState: mapGlobalState} = createNamespacedHelpers('global');
  export default {
    name: "BulkEditInvoiceModal",
    mixins: [InvoiceModalMixin],
    components: {
      CancelButton, SubmitButton,
      InputWidget, CurrencyInputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        model: new BulkEditInvoiceModel(),
        progress: null,
        showConfirmation: true
      }
    },
    computed: {
      ...mapState({
        modal: state => state.bulkEditInvoicesModal
      }),
      ...mapGlobalState(['currentBranch', 'inclusiveTaxForFinancialObjects', 'invoiceMiscellaneousItemCharges']),
      hasPredefinedMiscCharges() {
        return this.invoiceMiscellaneousItemCharges && this.invoiceMiscellaneousItemCharges.length > 1;
      },
    },
    methods: {
      ...mapActions(['hideBulkEditInvoicesModal', 'getTableData', 'showMarkItemsCollectedModal', 'getMarkItemsCollectedModalObject']),
      ...mapGlobalActions(['addProgress', 'updateProgressValue', 'removeProgress']),
      onHidden() {
        this.hideBulkEditInvoicesModal()
        this.model = new BulkEditInvoiceModel()
        this.progress = null
        this.loading = false
        this.showConfirmation = true
      },
      async onSubmit() {
        let cfc = this.currentBranch.tax_rate / 100 + 1

        this.model.resetErrors();
        this.loading = true

        const data = {
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.title || item.title_dropdown_value)
              && parseFloat(item.amount)).map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = parseFloat(item.amount / cfc).toFixed(2)
            }
            return item
          }),
          uuids: this.modal.invoices.map(item => item.uuid)}
        const response = await InvoicesService.bulkEditInvoices(data)

        this.loading = false;
        if (response.success) {
          this.showConfirmation = false
          this.hideBulkEditInvoicesModal();
          this.$toast('success', this.$i18n.t('Invoices updating successfully started'))
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.body.miscellaneous_items) {
            for (let index in response.body.miscellaneous_items) {
              this.model.miscellaneous_items[index].setMultipleErrors(response.body.miscellaneous_items[index]);
            }
          } else {
            this.$errorDialog(this.$i18n.t("An error occurred during invoice generation."));
          }
        }
      },
      onNewMiscellaneousItemClick() {
        this.model.miscellaneous_items.push(new MiscellaneousItem({taxable: true}))
      },
      onMiscellaneousItemDelete(index) {
        for (let i = 0; i < this.model.miscellaneous_items.length; i++) {
          if (i === index) {
            this.model.miscellaneous_items.splice(i, 1);
            break;
          }
        }
      },
      onMiscChargeDescriptionDropdownValueChange(item) {
        item.title = '';
      },
      async onInvoiceViewClick(invoice) {
        await InvoicesService.viewInvoicePdf(this, invoice, false, this.onMarkItemsCollectedClick)
      },
      async onMarkItemsCollectedClick(invoice) {
        await this.getMarkItemsCollectedModalObject({invoice});
        this.showMarkItemsCollectedModal();
      },
      async addProgressIfNotExist() {
        if (!this.progress) {
          this.progress = await this.addProgress(this.$i18n.t("Bulk Invoices Update Progress"));
        }
        return true
      },
      removeProgressIfExist() {
        if (this.progress) {
          this.removeProgress(this.progress.uuid)
          this.progress = null;
        }
      },
      async onProgressNotificationReceive(data) {
        await this.addProgressIfNotExist()
        this.updateProgressValue({
          progressValue: parseInt(Math.round(data.successful / data.total * 100)),
          uuid: this.progress.uuid
        });

        if (data.is_finished) {
          if (!data.errors) {
            this.$toast('success', this.$i18n.t("Invoices were successfully updated"));
            await this.getTableData()
          }
          this.removeProgressIfExist();
        }
        if (data.errors) {
          if (data.errors.non_field_errors) {
            this.$errorDialog(data.errors.non_field_errors[0])
          } else if (data.errors.errors) {
            const listItemsArr = [];
            data.errors.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
          } else {
            this.$errorDialog(this.$i18n.t("Something went wrong while updating invoices. Please, try again later."))
          }
        }
      },
    },
    mounted() {
      this.$ws.listen(`invoices.${WS_BULK_EDIT_INVOICES}`, this.onProgressNotificationReceive);
    },
    beforeDestroy() {
      this.removeProgressIfExist();
      this.$ws.remove(`invoices.${WS_BULK_EDIT_INVOICES}`);
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .miscellaneous-table {
    /deep/ .form-group {
      margin-top: .2em;
      margin-bottom: .5em;
    }

    tr {
      td {
        vertical-align: middle;
      }
    }
  }

  .invoices-table {
    tbody {
      td {
        vertical-align: middle;
        padding-top: .7em;
        padding-bottom: .7em;
      }
    }

    /deep/ .form-group {
      margin-bottom: 0;
    }
  }
</style>
